/**
 * Logging utility for Vue.js 2 applications
 * Follows Vue.js best practices for development vs production logging
 */

/**
 * Check if we're in development mode
 */
const isDevelopment = process.env.NODE_ENV !== 'production'

/**
 * Logger utility with different log levels
 * Respects Vue.js 2 development/production mode
 */
class Logger {
  constructor() {
    this.enabled = isDevelopment
    this.prefix = '[Honeycomb]'
  }

  /**
   * Log info messages (only in development)
   */
  info(message, ...args) {
    if (this.enabled) {
      // eslint-disable-next-line no-console
      console.log(`${this.prefix} ${message}`, ...args)
    }
  }

  /**
   * Log warning messages (only in development)
   */
  warn(message, ...args) {
    if (this.enabled) {
      // eslint-disable-next-line no-console
      console.warn(`${this.prefix} ${message}`, ...args)
    }
  }

  /**
   * Log error messages (always logged, even in production)
   */
  error(message, ...args) {
    // eslint-disable-next-line no-console
    console.error(`${this.prefix} ${message}`, ...args)
  }

  /**
   * Log debug messages (only in development)
   */
  debug(message, ...args) {
    if (this.enabled) {
      // eslint-disable-next-line no-console
      console.log(`${this.prefix} [DEBUG] ${message}`, ...args)
    }
  }

  /**
   * Create a scoped logger for specific components
   */
  scope(scopeName) {
    return {
      info: (message, ...args) => this.info(`[${scopeName}] ${message}`, ...args),
      warn: (message, ...args) => this.warn(`[${scopeName}] ${message}`, ...args),
      error: (message, ...args) => this.error(`[${scopeName}] ${message}`, ...args),
      debug: (message, ...args) => this.debug(`[${scopeName}] ${message}`, ...args)
    }
  }

  /**
   * Enable/disable logging programmatically
   */
  setEnabled(enabled) {
    this.enabled = enabled
  }
}

// Create singleton instance
export const logger = new Logger()

// Create scoped loggers for specific modules
export const schemaTableLogger = logger.scope('SchemaTable')
export const errorHandlerLogger = logger.scope('ErrorHandler')
export const validatorLogger = logger.scope('Validator')

// Export default instance
export default logger